import type {SerializedError} from '@reduxjs/toolkit';
import type {FetchBaseQueryError} from '@reduxjs/toolkit/query';
import {
  createContext,
  type Dispatch,
  memo,
  type ReactNode,
  useEffect,
  useMemo,
  useReducer,
} from 'react';
import {useSelector} from 'react-redux';
import {useGetGuestsQuery} from '~/ViewInvitation/api/endpoints/guests';
import {type Actions, ActionTypes} from '~/ViewInvitation/components/Guestlist/actions/actions';
import {useCurrentGuestId} from '~/ViewInvitation/components/Guestlist/hooks/useCurrentGuestId';
import {
  defaultSummaryValue,
  initialState,
  reducer,
  type State,
} from '~/ViewInvitation/components/Guestlist/reducer/reducer';
import {selectEventId} from '~/ViewInvitation/selectors/eventDetails';
import {selectRsvpResponse} from '~/ViewInvitation/selectors/rsvp';

interface GuestlistDataContextType {
  state: State;
  dispatch: Dispatch<Actions>;
  refetch: () => void;
}

export const GuestlistDataContext = createContext<GuestlistDataContextType | null>(null);

interface ContextProviderProps {
  children: ReactNode;
}

export const GuestlistDataContextProvider = memo(({children}: ContextProviderProps) => {
  const eventId = useSelector(selectEventId);
  const rsvpResponse = useSelector(selectRsvpResponse);
  const {guestId} = useCurrentGuestId();
  const [state, dispatch] = useReducer(reducer, initialState);
  const {data, refetch, isLoading, isError, error, isUninitialized} = useGetGuestsQuery(
    {
      eventId,
      guestId,
    },
    {refetchOnMountOrArgChange: true, skip: !guestId}
  );

  useEffect(() => {
    if (rsvpResponse && !isUninitialized) {
      refetch();
    }
  }, [rsvpResponse, refetch, isUninitialized]);

  useEffect(() => {
    if (isError) {
      if ('status' in error) {
        dispatch({type: ActionTypes.SET_ERROR, error: error as FetchBaseQueryError});
      } else {
        dispatch({type: ActionTypes.SET_ERROR, error: error as SerializedError});
      }
    } else {
      dispatch({type: ActionTypes.CLEAR_ERROR});
      if (!isLoading) {
        const guests = data?.guests || [];
        const summary = data?.summary || defaultSummaryValue;
        dispatch({type: ActionTypes.SET_GUESTS, guests, summary});
      }
    }
  }, [isError, isLoading, error, data]);

  const value = useMemo(() => ({state, dispatch, refetch}), [state, refetch]);

  return <GuestlistDataContext.Provider value={value}>{children}</GuestlistDataContext.Provider>;
});

GuestlistDataContextProvider.displayName = 'GuestlistDataContextProvider';
